// @flow
import * as React from "react";
import Transition from "react-transition-group/Transition";
import styles from "./FieldControl.scss";
import type { ReactElementRef } from "../../types/react";
import { isMdDown } from "../../utils/breakpoints";

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: { opacity: 0 },
  exited: { opacity: 0 }
};

type ErrorProps = {
  text: string,
  isVisible: boolean,
  isWrappingForced: boolean,
  forceErrorPositionAbsolute: boolean
};

type ErrorState = {
  isErrorWrapped: boolean
};

class Error extends React.Component<ErrorProps, ErrorState> {
  static defaultProps = {
    isWrappingForced: false,
    forceErrorPositionAbsolute: false
  };

  state = {
    isErrorWrapped: false
  };

  errorWrapper: ReactElementRef<HTMLDivElement> = React.createRef();

  componentDidMount() {
    this.setIsTextWrapped();
    window.addEventListener("resize", this.setIsTextWrapped);
  }

  componentDidUpdate({ text: prevText }: ErrorProps) {
    const { text: currentText } = this.props;
    const ifAppeared = !prevText && currentText;
    const ifChanged = prevText && prevText !== currentText;

    if (ifAppeared || ifChanged) {
      this.setIsTextWrapped();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.setIsTextWrapped);
  }

  setIsTextWrapped = () => {
    const errorWrapperElement = this.errorWrapper.current;

    if (!errorWrapperElement) {
      return;
    }

    const elementHeight = errorWrapperElement.offsetHeight;
    const elementLineHeight = parseInt(
      window.getComputedStyle(errorWrapperElement)["line-height"],
      10
    );
    const isWrapped = elementHeight > elementLineHeight;

    this.setState({ isErrorWrapped: isWrapped });
  };

  getPosition = () => {
    const { isWrappingForced, forceErrorPositionAbsolute } = this.props;
    const { isErrorWrapped } = this.state;

    // Update isMdDown if FormRow changed breakpoint for which inputs are displayed under each other
    if (forceErrorPositionAbsolute && isMdDown(window.innerWidth)) {
      return "absolute";
    }

    return isWrappingForced || isErrorWrapped ? "relative" : "absolute";
  };

  render() {
    const { text, isVisible } = this.props;

    return (
      <Transition in={isVisible} timeout={300} onEntered={this.setIsTextWrapped} unmountOnExit>
        {state => (
          <div
            ref={this.errorWrapper}
            style={{
              ...transitionStyles[state],
              position: this.getPosition()
            }}
            className={styles.error}
          >
            {text}
          </div>
        )}
      </Transition>
    );
  }
}

export default Error;
